import styled, { useTheme } from 'styled-components';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import deepmerge from 'deepmerge';
import React, { useState, useRef, useEffect } from 'react';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

var cssColorProperties = [
    'background',
    'backgroundColor',
    'borderColor',
    'borderTopColor',
    'borderBottomColor',
    'borderLeftColor',
    'borderRightColor',
    'color',
    'outlineColor',
    'fill',
    'stroke',
];

var standardLonghandProperties = [
    'alignContent',
    'alignItems',
    'alignSelf',
    'animationDelay',
    'animationDirection',
    'animationDuration',
    'animationFillMode',
    'animationIterationCount',
    'animationName',
    'animationPlayState',
    'animationTimingFunction',
    'appearance',
    'aspectRatio',
    'backdropFilter',
    'backfaceVisibility',
    'backgroundAttachment',
    'backgroundBlendMode',
    'backgroundClip',
    'backgroundColor',
    'backgroundImage',
    'backgroundOrigin',
    'backgroundPositionX',
    'backgroundPositionY',
    'backgroundRepeat',
    'backgroundSize',
    'blockOverflow',
    'blockSize',
    'borderBlockColor',
    'borderBlockEndColor',
    'borderBlockEndStyle',
    'borderBlockEndWidth',
    'borderBlockStartColor',
    'borderBlockStartStyle',
    'borderBlockStartWidth',
    'borderBlockStyle',
    'borderBlockWidth',
    'borderBottomColor',
    'borderBottomLeftRadius',
    'borderBottomRightRadius',
    'borderBottomStyle',
    'borderBottomWidth',
    'borderCollapse',
    'borderEndEndRadius',
    'borderEndStartRadius',
    'borderImageOutset',
    'borderImageRepeat',
    'borderImageSlice',
    'borderImageSource',
    'borderImageWidth',
    'borderInlineColor',
    'borderInlineEndColor',
    'borderInlineEndStyle',
    'borderInlineEndWidth',
    'borderInlineStartColor',
    'borderInlineStartStyle',
    'borderInlineStartWidth',
    'borderInlineStyle',
    'borderInlineWidth',
    'borderLeftColor',
    'borderLeftStyle',
    'borderLeftWidth',
    'borderRightColor',
    'borderRightStyle',
    'borderRightWidth',
    'borderSpacing',
    'borderStartEndRadius',
    'borderStartStartRadius',
    'borderTopColor',
    'borderTopLeftRadius',
    'borderTopRightRadius',
    'borderTopStyle',
    'borderTopWidth',
    'bottom',
    'boxDecorationBreak',
    'boxShadow',
    'boxSizing',
    'breakAfter',
    'breakBefore',
    'breakInside',
    'captionSide',
    'caretColor',
    'clear',
    'clipPath',
    'color',
    'colorAdjust',
    'columnCount',
    'columnFill',
    'columnGap',
    'columnRuleColor',
    'columnRuleStyle',
    'columnRuleWidth',
    'columnSpan',
    'columnWidth',
    'contain',
    'content',
    'counterIncrement',
    'counterReset',
    'counterSet',
    'cursor',
    'direction',
    'display',
    'emptyCells',
    'filter',
    'flexBasis',
    'flexDirection',
    'flexGrow',
    'flexShrink',
    'flexWrap',
    'float',
    'fontFamily',
    'fontFeatureSettings',
    'fontKerning',
    'fontLanguageOverride',
    'fontOpticalSizing',
    'fontSize',
    'fontSizeAdjust',
    'fontStretch',
    'fontStyle',
    'fontSynthesis',
    'fontVariant',
    'fontVariantCaps',
    'fontVariantEastAsian',
    'fontVariantLigatures',
    'fontVariantNumeric',
    'fontVariantPosition',
    'fontVariationSettings',
    'fontWeight',
    'gridAutoColumns',
    'gridAutoFlow',
    'gridAutoRows',
    'gridColumnEnd',
    'gridColumnStart',
    'gridRowEnd',
    'gridRowStart',
    'gridTemplateAreas',
    'gridTemplateColumns',
    'gridTemplateRows',
    'hangingPunctuation',
    'height',
    'hyphens',
    'imageOrientation',
    'imageRendering',
    'imageResolution',
    'initialLetter',
    'inlineSize',
    'inset',
    'insetBlock',
    'insetBlockEnd',
    'insetBlockStart',
    'insetInline',
    'insetInlineEnd',
    'insetInlineStart',
    'isolation',
    'justifyContent',
    'justifyItems',
    'justifySelf',
    'left',
    'letterSpacing',
    'lineBreak',
    'lineHeight',
    'lineHeightStep',
    'listStyleImage',
    'listStylePosition',
    'listStyleType',
    'marginBlock',
    'marginBlockEnd',
    'marginBlockStart',
    'marginBottom',
    'marginInline',
    'marginInlineEnd',
    'marginInlineStart',
    'marginLeft',
    'marginRight',
    'marginTop',
    'maskBorderMode',
    'maskBorderOutset',
    'maskBorderRepeat',
    'maskBorderSlice',
    'maskBorderSource',
    'maskBorderWidth',
    'maskClip',
    'maskComposite',
    'maskImage',
    'maskMode',
    'maskOrigin',
    'maskPosition',
    'maskRepeat',
    'maskSize',
    'maskType',
    'maxBlockSize',
    'maxHeight',
    'maxInlineSize',
    'maxLines',
    'maxWidth',
    'minBlockSize',
    'minHeight',
    'minInlineSize',
    'minWidth',
    'mixBlendMode',
    'motionDistance',
    'motionPath',
    'motionRotation',
    'objectFit',
    'objectPosition',
    'offsetAnchor',
    'offsetDistance',
    'offsetPath',
    'offsetRotate',
    'offsetRotation',
    'opacity',
    'order',
    'orphans',
    'outlineColor',
    'outlineOffset',
    'outlineStyle',
    'outlineWidth',
    'overflowAnchor',
    'overflowBlock',
    'overflowClipBox',
    'overflowInline',
    'overflowWrap',
    'overflowX',
    'overflowY',
    'overscrollBehaviorX',
    'overscrollBehaviorY',
    'paddingBlock',
    'paddingBlockEnd',
    'paddingBlockStart',
    'paddingBottom',
    'paddingInline',
    'paddingInlineEnd',
    'paddingInlineStart',
    'paddingLeft',
    'paddingRight',
    'paddingTop',
    'pageBreakAfter',
    'pageBreakBefore',
    'pageBreakInside',
    'paintOrder',
    'perspective',
    'perspectiveOrigin',
    'placeContent',
    'pointerEvents',
    'position',
    'quotes',
    'resize',
    'right',
    'rotate',
    'rowGap',
    'rubyAlign',
    'rubyMerge',
    'rubyPosition',
    'scale',
    'scrollBehavior',
    'scrollMargin',
    'scrollMarginBlock',
    'scrollMarginBlockEnd',
    'scrollMarginBlockStart',
    'scrollMarginBottom',
    'scrollMarginInline',
    'scrollMarginInlineEnd',
    'scrollMarginInlineStart',
    'scrollMarginLeft',
    'scrollMarginRight',
    'scrollMarginTop',
    'scrollPadding',
    'scrollPaddingBlock',
    'scrollPaddingBlockEnd',
    'scrollPaddingBlockStart',
    'scrollPaddingBottom',
    'scrollPaddingInline',
    'scrollPaddingInlineEnd',
    'scrollPaddingInlineStart',
    'scrollPaddingLeft',
    'scrollPaddingRight',
    'scrollPaddingTop',
    'scrollSnapAlign',
    'scrollSnapStop',
    'scrollSnapType',
    'scrollbarColor',
    'scrollbarWidth',
    'shapeImageThreshold',
    'shapeMargin',
    'shapeOutside',
    'tabSize',
    'tableLayout',
    'textAlign',
    'textAlignLast',
    'textCombineUpright',
    'textDecorationColor',
    'textDecorationLine',
    'textDecorationSkip',
    'textDecorationSkipInk',
    'textDecorationStyle',
    'textDecorationThickness',
    'textDecorationWidth',
    'textEmphasisColor',
    'textEmphasisPosition',
    'textEmphasisStyle',
    'textIndent',
    'textJustify',
    'textOrientation',
    'textOverflow',
    'textRendering',
    'textShadow',
    'textSizeAdjust',
    'textTransform',
    'textUnderlineOffset',
    'textUnderlinePosition',
    'top',
    'touchAction',
    'transform',
    'transformBox',
    'transformOrigin',
    'transformStyle',
    'transitionDelay',
    'transitionDuration',
    'transitionProperty',
    'transitionTimingFunction',
    'translate',
    'unicodeBidi',
    'userSelect',
    'verticalAlign',
    'visibility',
    'whiteSpace',
    'widows',
    'width',
    'willChange',
    'wordBreak',
    'wordSpacing',
    'wordWrap',
    'writingMode',
    'zIndex',
    'zoom',
];

var standardShorthandProperties = [
    'all',
    'animation',
    'background',
    'backgroundPosition',
    'border',
    'borderBlock',
    'borderBlockEnd',
    'borderBlockStart',
    'borderBottom',
    'borderColor',
    'borderImage',
    'borderInline',
    'borderInlineEnd',
    'borderInlineStart',
    'borderLeft',
    'borderRadius',
    'borderRight',
    'borderStyle',
    'borderTop',
    'borderWidth',
    'columnRule',
    'columns',
    'flex',
    'flexFlow',
    'font',
    'gap',
    'grid',
    'gridArea',
    'gridColumn',
    'gridRow',
    'gridTemplate',
    'lineClamp',
    'listStyle',
    'margin',
    'mask',
    'maskBorder',
    'motion',
    'offset',
    'outline',
    'overflow',
    'overscrollBehavior',
    'padding',
    'placeItems',
    'placeSelf',
    'textDecoration',
    'textEmphasis',
    'transition',
];

var standardProperties = __spreadArray(__spreadArray([], standardLonghandProperties, true), standardShorthandProperties, true);
var cssProperties = __spreadArray([], standardProperties, true);

var cssSpacingProperties = [
    'margin',
    'marginTop',
    'marginBottom',
    'marginLeft',
    'marginRight',
    'padding',
    'paddingTop',
    'paddingBottom',
    'paddingLeft',
    'paddingRight',
    'gap',
    'rowGap',
    'columnGap',
    'gridGap',
    'gridRowGap',
    'gridColumnGap',
];

var addStyleProp = function (style, key, value, theme) {
    var _a, _b;
    var returnStyle = style || {};
    if (cssColorProperties.includes(key)) {
        returnStyle[key] = ((_a = theme === null || theme === void 0 ? void 0 : theme.colors) === null || _a === void 0 ? void 0 : _a[value]) || value;
        return returnStyle;
    }
    if (cssSpacingProperties.includes(key)) {
        returnStyle[key] = ((_b = theme === null || theme === void 0 ? void 0 : theme.spacings) === null || _b === void 0 ? void 0 : _b[value]) || value;
        return returnStyle;
    }
    returnStyle[key] = value;
    return returnStyle;
};
var UiElement = styled.div.withConfig({
    shouldForwardProp: function (prop) { return !cssProperties.includes(prop) && prop !== 'csso'; },
})(function (_a) {
    var csso = _a.csso, theme = _a.theme, props = __rest(_a, ["csso", "theme"]);
    var baseStyle = __assign({}, csso);
    var isMediaQuery = false;
    Object.keys(props).forEach(function (propKey) {
        if (cssProperties.includes(propKey)) {
            var propValue_1 = props[propKey];
            if (typeof propValue_1 == 'object') {
                Object.keys(propValue_1).forEach(function (propObjectKey) {
                    var _a, _b;
                    var propObjectValue = propValue_1[propObjectKey];
                    if (propObjectKey === '_') {
                        baseStyle = addStyleProp(baseStyle, propKey, propObjectValue, theme);
                    }
                    else if (((_a = theme === null || theme === void 0 ? void 0 : theme.screens) === null || _a === void 0 ? void 0 : _a[propObjectKey]) !== undefined) {
                        var screenValue = (_b = theme === null || theme === void 0 ? void 0 : theme.screens) === null || _b === void 0 ? void 0 : _b[propObjectKey];
                        if (!screenValue || screenValue === '0' || screenValue === '0px') {
                            if (typeof propObjectValue == 'object') {
                                Object.keys(propObjectValue).forEach(function (propSubObjectKey) {
                                    var propSubObjectValue = propObjectValue[propSubObjectKey];
                                    if (propSubObjectKey === '_') {
                                        baseStyle = addStyleProp(baseStyle, propKey, propSubObjectValue, theme);
                                    }
                                    else if (propSubObjectKey.startsWith(':')) {
                                        var pseudoProp = "&".concat(propSubObjectKey);
                                        baseStyle[pseudoProp] = addStyleProp(baseStyle[pseudoProp], propKey, propSubObjectValue, theme);
                                    }
                                });
                            }
                            else {
                                baseStyle = addStyleProp(baseStyle, propKey, propObjectValue, theme);
                            }
                        }
                        else {
                            Object.values(theme.screens).forEach(function (themeScreenValue) {
                                var mediaQuery = "@media (min-width: ".concat(themeScreenValue, ")");
                                if (!baseStyle[mediaQuery]) {
                                    baseStyle[mediaQuery] = {};
                                }
                            });
                            isMediaQuery = true;
                            var mediaQueryProp = "@media (min-width: ".concat(screenValue, ")");
                            var mediaQueryStyle_1 = baseStyle[mediaQueryProp] || {};
                            if (typeof propObjectValue == 'object') {
                                Object.keys(propObjectValue).forEach(function (propSubObjectKey) {
                                    var propSubObjectValue = propObjectValue[propSubObjectKey];
                                    if (propSubObjectKey === '_') {
                                        mediaQueryStyle_1 = addStyleProp(mediaQueryStyle_1, propKey, propSubObjectValue, theme);
                                    }
                                    else if (propSubObjectKey.startsWith(':')) {
                                        var pseudoProp = "&".concat(propSubObjectKey);
                                        mediaQueryStyle_1[pseudoProp] = addStyleProp(mediaQueryStyle_1[pseudoProp], propKey, propSubObjectValue, theme);
                                    }
                                });
                                baseStyle[mediaQueryProp] = mediaQueryStyle_1;
                            }
                            else {
                                baseStyle[mediaQueryProp] = addStyleProp(baseStyle[mediaQueryProp], propKey, propObjectValue, theme);
                            }
                        }
                    }
                    else if (propObjectKey.startsWith(':')) {
                        var pseudoProp = "&".concat(propObjectKey);
                        baseStyle[pseudoProp] = addStyleProp(baseStyle[pseudoProp], propKey, propObjectValue, theme);
                    }
                });
            }
            else {
                baseStyle = addStyleProp(baseStyle, propKey, propValue_1, theme);
            }
        }
    });
    if (isMediaQuery) {
        Object.values(theme.screens).forEach(function (screenValue) {
            var mediaQueryProp = "@media (min-width: ".concat(screenValue, ")");
            if (Object.keys(baseStyle[mediaQueryProp]).length === 0) {
                delete baseStyle[mediaQueryProp];
            }
        });
    }
    return baseStyle;
});

var UiDiv = function (_a) {
    var innerRef = _a.innerRef, children = _a.children, props = __rest(_a, ["innerRef", "children"]);
    return (jsx(UiElement, __assign({ as: "div" }, props, { ref: innerRef }, { children: children })));
};

var UiFlex = function (_a) {
    var innerRef = _a.innerRef, children = _a.children, props = __rest(_a, ["innerRef", "children"]);
    return (jsx(UiElement, __assign({ as: "div", display: "flex" }, props, { ref: innerRef }, { children: children })));
};

var UiInput = function (_a) {
    var innerRef = _a.innerRef, children = _a.children, props = __rest(_a, ["innerRef", "children"]);
    return (jsx(UiElement, __assign({ as: "input", ref: innerRef }, props, { children: children })));
};

var UiText = function (_a) {
    var _b;
    var variant = _a.variant, as = _a.as, innerRef = _a.innerRef, children = _a.children, props = __rest(_a, ["variant", "as", "innerRef", "children"]);
    var theme = useTheme();
    return (jsx(UiElement, __assign({ as: as || 'div', csso: variant ? (_b = theme === null || theme === void 0 ? void 0 : theme.textVariants) === null || _b === void 0 ? void 0 : _b[variant] : undefined, ref: innerRef }, props, { children: children })));
};

var UiSpan = function (_a) {
    var _b;
    var textVariant = _a.textVariant, as = _a.as, innerRef = _a.innerRef, children = _a.children, props = __rest(_a, ["textVariant", "as", "innerRef", "children"]);
    var theme = useTheme();
    return (jsx(UiElement, __assign({ as: as || 'span', csso: textVariant ? (_b = theme === null || theme === void 0 ? void 0 : theme.textVariants) === null || _b === void 0 ? void 0 : _b[textVariant] : undefined, ref: innerRef }, props, { children: children })));
};

var Block = function (_a) {
    var display = _a.display, boxSizing = _a.boxSizing, justify = _a.justify, justifyContent = _a.justifyContent, alignItems = _a.alignItems, children = _a.children, theme = _a.theme, props = __rest(_a, ["display", "boxSizing", "justify", "justifyContent", "alignItems", "children", "theme"]);
    var isFlexProp = !!justify || !!justifyContent || !!alignItems;
    return (jsx(UiDiv, __assign({ display: display || isFlexProp ? 'flex' : undefined, boxSizing: boxSizing || 'border-box', justifyContent: justifyContent || justify, alignItems: alignItems, csso: theme === null || theme === void 0 ? void 0 : theme.root }, props, { children: children })));
};

var COLORS = {
    DARK: '#333333',
    LIGHT: '#f6f6f6',
    GREY: '#dddddd',
    GREY_HOVER: '#cccccc',
    GREY_ACTIVE: '#666666',
    GREY_TEXT: '#999999',
    PRIMARY: 'orange',
    PRIMARY_HOVER: 'darkorange',
    SECONDARY: 'skyblue',
    SECONDARY_HOVER: 'deepskyblue',
    dark: '#333333',
    light: '#f6f6f6',
    grey: '#dddddd',
    greyHover: '#cccccc',
    greyActive: '#666666',
    greyText: '#999999',
    primary: 'orange',
    primaryHover: 'darkorange',
    secondary: 'skyblue',
    secondaryHover: 'deepskyblue',
};
var SPACINGS = {
    XXS: '5px',
    XS: '10px',
    S: '20px',
    M: '30px',
    L: '50px',
    XL: '80px',
    XXL: '130px',
    xxs: '5px',
    xs: '10px',
    s: '20px',
    m: '30px',
    l: '50px',
    xl: '80px',
    xxl: '130px',
};
var TEXTSTYLE_SMALL = {
    fontFamily: '"Source Sans Pro", sans-serif',
    fontSize: '14px',
    lineHeight: '20px',
};
var TEXTSTYLE_MEDIUM = {
    fontFamily: '"Source Sans Pro", sans-serif',
    fontSize: '16px',
    lineHeight: '22px',
};
var COMMON_STYLES = {
    borderRadius: '3px',
    boxShadowDropdown: '0 1px 4px 0px rgba(0, 0, 0, 0.2)',
};

var styleDefault = __assign(__assign({ appearance: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', background: COLORS.GREY, border: 0, margin: 0, padding: '10px 15px' }, TEXTSTYLE_MEDIUM), { whiteSpace: 'nowrap', cursor: 'pointer', span: {
        display: 'inline-flex',
        '&:first-of-type': {
            marginLeft: 0,
        },
    }, svg: {
        width: '22px',
        height: '22px',
        verticalAlign: 'top',
    }, '&[data-size-small="true"]': __assign(__assign({ padding: '5px 12px' }, TEXTSTYLE_SMALL), { span: {
            display: 'inline-flex',
            '&:first-of-type': {
                marginLeft: 0,
            },
        }, svg: {
            width: '18px',
            height: '18px',
            verticalAlign: 'top',
        } }), '&[data-block="true"]': {
        display: 'flex',
        width: '100%',
    }, '&[data-disabled="true"]': {
        opacity: 0.5,
        cursor: 'default',
        pointerEvents: 'none',
    } });
var themeLayoutPrimary = {
    background: COLORS.PRIMARY,
    '&:focus': {
        outline: 0,
        background: COLORS.DARK,
        color: 'white',
    },
    '&:hover': {
        background: COLORS.PRIMARY_HOVER,
        color: COLORS.DARK,
    },
    '&:active': {
        background: COLORS.PRIMARY,
        color: COLORS.DARK,
    },
};
var themeLayoutSecondary = {
    background: COLORS.SECONDARY,
    '&:focus': {
        outline: 0,
        background: COLORS.DARK,
        color: 'white',
    },
    '&:hover': {
        background: COLORS.SECONDARY_HOVER,
        color: COLORS.DARK,
    },
    '&:active': {
        background: COLORS.SECONDARY,
        color: COLORS.DARK,
    },
};
var themeLayoutOutlined = {
    background: 'transparent',
    boxShadow: "inset 0px 0px 0px 1px ".concat(COLORS.GREY),
    '&:focus': {
        outline: 0,
        background: COLORS.DARK,
        color: 'white',
        boxShadow: 'none',
    },
    '&:hover': {
        background: COLORS.LIGHT,
        color: COLORS.DARK,
        boxShadow: "inset 0px 0px 0px 1px ".concat(COLORS.GREY_HOVER),
    },
    '&:active': {
        background: 'transparent',
        color: COLORS.DARK,
        boxShadow: "inset 0px 0px 0px 1px ".concat(COLORS.GREY),
    },
};
var themeLayoutOption = {
    background: 'transparent',
    fontWeight: 400,
    '&[data-active="true"]': {
        background: 'transparent',
        color: COLORS.PRIMARY,
        '&:hover': {
            background: COLORS.LIGHT,
            color: COLORS.PRIMARY,
        },
        '&:active': {
            background: 'transparent',
            color: COLORS.PRIMARY,
        },
    },
    '&:focus': {
        outline: 0,
        background: COLORS.DARK,
        color: 'white',
    },
    '&:hover': {
        background: COLORS.LIGHT,
        color: COLORS.DARK,
    },
    '&:active': {
        background: 'transparent',
        color: COLORS.DARK,
    },
};
var themeLayoutDefault = {
    borderRadius: COMMON_STYLES.borderRadius,
    color: COLORS.DARK,
    fontWeight: 600,
    textDecoration: 'none',
    textAlign: 'left',
    span: {
        marginLeft: '7px',
    },
    '&:focus': {
        outline: 0,
        background: COLORS.DARK,
        color: 'white',
    },
    '&:hover': {
        background: COLORS.GREY_HOVER,
        color: COLORS.DARK,
    },
    '&:active': {
        background: COLORS.GREY,
        color: COLORS.DARK,
    },
    '&[data-with-icon="true"]': {
        padding: '10px 15px 10px 12px',
    },
    '&[data-with-icon-right="true"]': {
        padding: '10px 12px 10px 15px',
    },
    '&[data-with-icon-only="true"]': {
        padding: '10px',
    },
    '&[data-size-small="true"]': {
        span: {
            marginLeft: '5px',
        },
        '&[data-with-icon]': {
            padding: '5px 12px 5px 9px',
        },
        '&[data-with-icon-right]': {
            padding: '5px 9px 5px 12px',
        },
        '&[data-with-icon-only]': {
            padding: '6px',
        },
    },
    '&[data-layout-primary="true"]': themeLayoutPrimary,
    '&[data-layout-secondary="true"]': themeLayoutSecondary,
    '&[data-layout-outlined="true"]': themeLayoutOutlined,
    '&[data-layout-option="true"]': themeLayoutOption,
    '&[data-active="true"]': {
        background: COLORS.GREY_ACTIVE,
        color: 'white',
        boxShadow: 'none',
        '&:focus': {
            outline: 0,
            background: COLORS.DARK,
            color: 'white',
        },
        '&:hover': {
            background: COLORS.DARK,
            color: 'white',
            boxShadow: 'none',
        },
        '&:active': {
            background: COLORS.GREY_ACTIVE,
            color: 'white',
            boxShadow: 'none',
        },
    },
};

var StyledButton = styled.button(function (_a) {
    var jss = _a.jss;
    return jss;
});
var StyledLink = styled.a(function (_a) {
    var jss = _a.jss;
    return jss;
});
var Button = function (_a) {
    var _b;
    var layout = _a.layout, size = _a.size, _c = _a.type, type = _c === void 0 ? 'button' : _c, label = _a.label, href = _a.href, target = _a.target, title = _a.title, disabled = _a.disabled, icon = _a.icon, iconRight = _a.iconRight, block = _a.block, _d = _a.justify, justify = _d === void 0 ? 'center' : _d, active = _a.active, ariaLabel = _a.ariaLabel, className = _a.className, style = _a.style, theme = _a.theme, stopPropagation = _a.stopPropagation, onClick = _a.onClick, onMouseUp = _a.onMouseUp, onMouseDown = _a.onMouseDown;
    var handleOnClick = function (e) {
        if (href && disabled) {
            e.preventDefault();
        }
        if (stopPropagation) {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
        }
        if (!disabled) {
            typeof onClick === 'function' && onClick(e);
        }
    };
    var handleOnMouseLeave = function (e) {
        e.currentTarget.blur();
    };
    var data = (_b = {
            'data-active': active || undefined,
            'data-disabled': disabled || undefined,
            'data-with-icon': Boolean(label && icon) || undefined,
            'data-with-icon-right': Boolean(label && iconRight) || undefined,
            'data-with-icon-only': Boolean(!label && icon) || undefined
        },
        _b["data-size-".concat(size)] = !!size || undefined,
        _b["data-layout-".concat(layout)] = !!layout || undefined,
        _b['data-block'] = block || undefined,
        _b);
    var rootClassName = className || 'ui-button';
    var jss = __assign({}, styleDefault);
    if (justify) {
        jss.justifyContent = justify;
    }
    var rootTheme = theme ? theme.root : null;
    if (rootTheme) {
        jss = deepmerge(jss, rootTheme);
    }
    else {
        jss = deepmerge(jss, themeLayoutDefault);
    }
    var settings = {
        title: title,
        'aria-label': ariaLabel,
        onClick: handleOnClick,
        onMouseLeave: handleOnMouseLeave,
        onMouseUp: onMouseUp,
        onMouseDown: onMouseDown,
    };
    var contents = (jsxs(Fragment, { children: [icon && jsx("span", __assign({ className: "".concat(rootClassName, "__icon") }, { children: icon })), label && jsx("span", __assign({ className: "".concat(rootClassName, "__label") }, { children: label })), iconRight && jsx("span", __assign({ className: "".concat(rootClassName, "__icon-right") }, { children: iconRight }))] }));
    if (href) {
        return (jsx(StyledLink, __assign({ href: href, target: (href ? target : undefined) }, settings, data, { jss: jss, style: style, className: rootClassName }, { children: contents })));
    }
    return (jsx(StyledButton, __assign({ type: (!href ? type : undefined), disabled: disabled }, settings, data, { jss: jss, style: style, className: rootClassName }, { children: contents })));
};

var StyledGroupItem = styled.div(function (_a) {
    var jss = _a.jss;
    return jss;
});
var GroupItemLegacy = function (_a) {
    var spacing = _a.spacing, hSpacing = _a.hSpacing, vSpacing = _a.vSpacing, grow = _a.grow, width = _a.width, minWidth = _a.minWidth, maxWidth = _a.maxWidth, vertical = _a.vertical, children = _a.children;
    var paddingRight = undefined;
    var paddingBottom = undefined;
    if (SPACINGS[spacing]) {
        paddingRight = SPACINGS[spacing];
        paddingBottom = SPACINGS[spacing];
    }
    else if (spacing) {
        paddingRight = spacing;
        paddingBottom = spacing;
    }
    if (SPACINGS[hSpacing]) {
        paddingRight = SPACINGS[hSpacing];
    }
    else if (hSpacing) {
        paddingRight = hSpacing;
    }
    if (SPACINGS[vSpacing]) {
        paddingBottom = SPACINGS[vSpacing];
    }
    else if (vSpacing) {
        paddingBottom = vSpacing;
    }
    var flexGrow = undefined;
    if (!vertical && grow) {
        flexGrow = 1;
    }
    var jss = {
        width: width,
        minWidth: minWidth,
        maxWidth: maxWidth,
        paddingRight: paddingRight,
        paddingBottom: paddingBottom,
        flexGrow: flexGrow,
    };
    return jsx(StyledGroupItem, __assign({ jss: jss }, { children: children }));
};

var StyledWrapper$2 = styled.div(function (_a) {
    var jss = _a.jss;
    return jss;
});
var StyledGroup = styled.div(function (_a) {
    var jss = _a.jss;
    return jss;
});
var GroupLegacy = function (_a) {
    var spacing = _a.spacing, hSpacing = _a.hSpacing, vSpacing = _a.vSpacing, justify = _a.justify, alignItems = _a.alignItems, _b = _a.grow, grow = _b === void 0 ? false : _b, _c = _a.nowrap, nowrap = _c === void 0 ? false : _c, _d = _a.vertical, vertical = _d === void 0 ? false : _d, width = _a.width, background = _a.background, padding = _a.padding, paddingTop = _a.paddingTop, paddingLeft = _a.paddingLeft, paddingRight = _a.paddingRight, paddingBottom = _a.paddingBottom, children = _a.children;
    var aChildren = React.Children.toArray(children);
    var jssWrapper = {
        display: 'flex',
        boxSizing: 'border-box',
        width: width,
        padding: SPACINGS[padding] || padding,
        paddingTop: SPACINGS[paddingTop] || paddingTop,
        paddingLeft: SPACINGS[paddingLeft] || paddingLeft,
        paddingRight: SPACINGS[paddingRight] || paddingRight,
        paddingBottom: SPACINGS[paddingBottom] || paddingBottom,
        background: COLORS[background] || background,
    };
    var marginRight = undefined;
    var marginBottom = undefined;
    if (SPACINGS[spacing]) {
        marginRight = "-".concat(SPACINGS[spacing]);
        marginBottom = "-".concat(SPACINGS[spacing]);
    }
    else if (spacing) {
        marginRight = "-".concat(spacing);
        marginBottom = "-".concat(spacing);
    }
    if (SPACINGS[hSpacing]) {
        marginRight = "-".concat(SPACINGS[hSpacing]);
    }
    else if (hSpacing) {
        marginRight = "-".concat(hSpacing);
    }
    if (SPACINGS[vSpacing]) {
        marginBottom = "-".concat(SPACINGS[vSpacing]);
    }
    else if (vSpacing) {
        marginBottom = "-".concat(vSpacing);
    }
    var flexFlow = 'row wrap';
    if (!vertical && nowrap) {
        flexFlow = 'row nowrap';
    }
    var display = 'flex';
    if (vertical) {
        display = 'block';
    }
    var jssGroup = {
        flexGrow: 1,
        display: display,
        flexFlow: flexFlow,
        justifyContent: !vertical ? justify : undefined,
        alignItems: !vertical && alignItems ? alignItems : undefined,
        marginRight: marginRight,
        marginBottom: marginBottom,
    };
    return (jsx(StyledWrapper$2, __assign({ jss: jssWrapper }, { children: jsx(StyledGroup, __assign({ jss: jssGroup }, { children: aChildren.map(function (child, i) {
                if (!child) {
                    return false;
                }
                var childSettings = {
                    spacing: spacing,
                    hSpacing: hSpacing,
                    vSpacing: vSpacing,
                    grow: grow,
                    vertical: vertical,
                };
                var childAsReactElement = child;
                if (childAsReactElement.type === GroupItemLegacy) {
                    return React.cloneElement(childAsReactElement, __assign(__assign({ key: String(i) }, childSettings), { grow: childAsReactElement.props.grow || grow }));
                }
                return (jsx(GroupItemLegacy, __assign({}, childSettings, { children: child }), String(i)));
            }) })) })));
};

var Check = (jsx("svg", __assign({ viewBox: "0 0 24 24" }, { children: jsx("path", { fill: "currentColor", d: "M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" }) })));

var StyledRoot$1 = styled.div({
    position: 'relative',
    '&[data-disabled="true"]': {
        opacity: 0.5,
        pointerEvents: 'none',
    },
});
var StyledInput$2 = styled.input({
    appearance: 'none',
    position: 'absolute',
    top: 0,
    left: 0,
    height: 0,
    width: 0,
    margin: 0,
    padding: 0,
    fontSize: 0,
    lineHeight: 0,
    opacity: 0,
});
var StyledCheckbox = styled.div(function (_a) {
    var jss = _a.jss;
    return jss;
});
var StyledLabel$1 = styled.div(function (_a) {
    var jss = _a.jss;
    return jss;
});
var Checkbox = function (_a) {
    var id = _a.id, name = _a.name, value = _a.value, label = _a.label, ariaLabel = _a.ariaLabel, _b = _a.checked, checked = _b === void 0 ? false : _b, _c = _a.disabled, disabled = _c === void 0 ? false : _c, _d = _a.spacing, spacing = _d === void 0 ? 'xs' : _d, icon = _a.icon, onChange = _a.onChange, theme = _a.theme;
    var _e = useState(checked), isChecked = _e[0], setIsChecked = _e[1];
    var _f = useState(false), isFocus = _f[0], setIsFocus = _f[1];
    var refInput = useRef(null);
    useEffect(function () {
        setIsChecked(checked);
    }, [checked]);
    var handleOnInputChange = function (e) {
        setIsChecked(e.target.checked);
        if (typeof onChange === 'function')
            onChange(e);
    };
    var handleOnClickCheckbox = function () {
        if (refInput && refInput.current) {
            refInput.current.click();
            refInput.current.focus();
        }
    };
    var handleOnFocus = function () {
        setIsFocus(true);
    };
    var handleOnBlur = function () {
        setIsFocus(false);
    };
    var data = {
        'data-focus': isFocus || undefined,
        'data-checked': isChecked || undefined,
        'data-disabled': disabled || undefined,
    };
    var jssCheckbox = {
        flex: '0 0 auto',
        width: '22px',
        height: '22px',
        border: "1px solid ".concat(COLORS.GREY_TEXT),
        boxSizing: 'border-box',
        background: 'white',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        svg: {
            width: '18px',
            height: '18px',
            opacity: 0,
        },
        '&[data-checked="true"]': {
            svg: {
                opacity: 1,
            },
        },
    };
    if (theme === null || theme === void 0 ? void 0 : theme.checkbox) {
        jssCheckbox = deepmerge(jssCheckbox, theme === null || theme === void 0 ? void 0 : theme.checkbox);
    }
    else {
        jssCheckbox = deepmerge(jssCheckbox, {
            color: COLORS.DARK,
            '&[data-focus="true"]': {
                borderColor: COLORS.DARK,
            },
            '&[data-checked="true"]': {
                svg: {
                    color: COLORS.PRIMARY,
                },
            },
        });
    }
    var jssLabel = __assign({ display: 'inline-block' }, TEXTSTYLE_MEDIUM);
    if (theme === null || theme === void 0 ? void 0 : theme.label) {
        jssLabel = deepmerge(jssLabel, theme.label);
    }
    return (jsxs(StyledRoot$1, __assign({}, data, { children: [jsx(StyledInput$2, { ref: refInput, id: id, type: "checkbox", name: name, value: value, checked: isChecked, "aria-label": (ariaLabel || label), disabled: disabled, onChange: handleOnInputChange, onFocus: handleOnFocus, onBlur: handleOnBlur }), jsxs(GroupLegacy, __assign({ nowrap: true, spacing: spacing }, { children: [jsx(StyledCheckbox, __assign({ jss: jssCheckbox, onClick: handleOnClickCheckbox }, data, { children: icon || Check })), label && (jsx(StyledLabel$1, __assign({ jss: jssLabel }, data, { children: label })))] }))] })));
};

var Collapse = function (_a) {
    var _b = _a.show, show = _b === void 0 ? false : _b, _c = _a.duration, duration = _c === void 0 ? 200 : _c, _d = _a.animationType, animationType = _d === void 0 ? 'ease' : _d, _e = _a.fadeInOut, fadeInOut = _e === void 0 ? false : _e, _f = _a.keepChildren, keepChildren = _f === void 0 ? true : _f, children = _a.children;
    var _g = useState(show), isShown = _g[0], setIsShown = _g[1];
    var _h = useState(keepChildren || isShown), renderContent = _h[0], setRenderContent = _h[1];
    var _j = useState(show ? 'none' : 0), maxHeight = _j[0], setMaxHeight = _j[1];
    var _k = useState(show ? 'visible' : 'hidden'), overflow = _k[0], setOverflow = _k[1];
    var _l = useState(undefined), timeoutIdShow = _l[0], setTimeoutIdShow = _l[1];
    var _m = useState(undefined), timeoutIdHide = _m[0], setTimeoutIdHide = _m[1];
    var refComponent = useRef(null);
    var refWrapper = useRef(null);
    useEffect(function () {
        var _a, _b;
        var componentHeight = ((_a = refComponent === null || refComponent === void 0 ? void 0 : refComponent.current) === null || _a === void 0 ? void 0 : _a.clientHeight) || 0;
        var wrapperHeight = ((_b = refWrapper === null || refWrapper === void 0 ? void 0 : refWrapper.current) === null || _b === void 0 ? void 0 : _b.clientHeight) || 0;
        if (!keepChildren && isShown && !renderContent) {
            setRenderContent(true);
        }
        if (isShown && renderContent) {
            if (componentHeight === wrapperHeight) {
                setMaxHeight('none');
                setOverflow('visible');
            }
            if (componentHeight !== wrapperHeight) {
                setMaxHeight("".concat(wrapperHeight, "px"));
                setOverflow('hidden');
                setTimeoutIdShow(window.setTimeout(function () {
                    setMaxHeight('none');
                    setOverflow('visible');
                }, duration));
            }
        }
        if (!isShown) {
            if (componentHeight === 0) {
                setMaxHeight(0);
                setOverflow('hidden');
            }
            if (componentHeight > 0) {
                setMaxHeight("".concat(wrapperHeight, "px"));
                setTimeoutIdHide(window.setTimeout(function () {
                    setMaxHeight(0);
                    setOverflow('hidden');
                    if (!keepChildren) {
                        setTimeoutIdShow(window.setTimeout(function () {
                            setRenderContent(false);
                        }, duration));
                    }
                }, 50));
            }
        }
    }, [isShown, renderContent, duration, keepChildren]);
    useEffect(function () {
        setIsShown(show);
    }, [show]);
    useEffect(function () {
        return function cleanup() {
            window.clearTimeout(timeoutIdShow);
            window.clearTimeout(timeoutIdHide);
        };
    }, [timeoutIdShow, timeoutIdHide]);
    var style = {
        maxHeight: maxHeight,
        overflow: overflow,
        opacity: !show && fadeInOut ? 0 : 1,
        transition: "max-height ".concat(duration / 1000, "s ").concat(animationType, ", opacity ").concat(duration / 1000, "s ease"),
    };
    return (jsx("div", __assign({ style: style, ref: refComponent }, { children: jsx("div", __assign({ ref: refWrapper }, { children: renderContent && children })) })));
};

var useResizeObserver = function (_a) {
    var disabled = _a.disabled, element = _a.element, onResize = _a.onResize;
    var current = element === null || element === void 0 ? void 0 : element.current;
    var observer = useRef(null);
    useEffect(function () {
        if (!disabled) {
            var observe = function () {
                if (element && element.current && observer && observer.current) {
                    observer.current.observe(element === null || element === void 0 ? void 0 : element.current);
                }
            };
            if (observer && observer.current && current) {
                observer.current.unobserve(current);
            }
            observer.current = new ResizeObserver(onResize);
            observe();
        }
        return function () {
            if (observer && observer.current && element && current) {
                observer.current.unobserve(current);
            }
        };
    }, [disabled, current, element, onResize]);
};

var StyledComponent$1 = styled.div({
    position: 'relative',
    paddingTop: '100%',
    overflow: 'hidden',
});
var StyledWrapper$1 = styled.div({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
});
var StyledImage$1 = styled.img(function (_a) {
    var _b = _a.fadeIn, fadeIn = _b === void 0 ? false : _b, _c = _a.legacyMode, legacyMode = _c === void 0 ? false : _c;
    return {
        position: 'absolute',
        top: legacyMode ? '-1000%' : 0,
        bottom: legacyMode ? '-1000%' : 0,
        left: legacyMode ? '-1000%' : 0,
        right: legacyMode ? '-1000%' : 0,
        margin: 'auto',
        verticalAlign: 'top',
        width: '100%',
        height: '100%',
        objectFit: !legacyMode ? 'cover' : 'fill',
        opacity: fadeIn ? 0 : 1,
        transition: fadeIn ? 'opacity 0.3s ease' : '',
        '&[data-loaded="true"]': {
            opacity: 1,
        },
    };
});
var CroppedImage = function (props) {
    var src = props.src, srcWebP = props.srcWebP, alt = props.alt, ratio = props.ratio, width = props.width, height = props.height, background = props.background, srcSet = props.srcSet, sizes = props.sizes, _a = props.fadeIn, fadeIn = _a === void 0 ? false : _a, _b = props.legacyMode, legacyMode = _b === void 0 ? false : _b, children = props.children, onImageLoaded = props.onImageLoaded, onImageResize = props.onImageResize;
    var _c = useState(false), isImageLoaded = _c[0], setIsImageLoaded = _c[1];
    var _d = useState(0), naturalWidth = _d[0], setNaturalWidth = _d[1];
    var _e = useState(0), naturalHeight = _e[0], setNaturalHeight = _e[1];
    var _f = useState(0), componentWidth = _f[0], setComponentWidth = _f[1];
    var _g = useState(0), componentHeight = _g[0], setComponentHeight = _g[1];
    var wrapperRef = useRef(null);
    var imgRef = useRef(null);
    var _ratio = ratio || '1:1';
    var _h = _ratio.split(':'), w = _h[0], h = _h[1];
    var ratioWidth = parseFloat(w) || 1;
    var ratioHeight = parseFloat(h) || 1;
    useResizeObserver({
        element: wrapperRef,
        onResize: function (resizeObserverEntries) {
            if (legacyMode) {
                var entry = resizeObserverEntries[0];
                if (entry) {
                    var contentRect = entry.contentRect || {};
                    setComponentWidth(contentRect.width);
                    setComponentHeight(contentRect.height);
                }
            }
            if (imgRef && imgRef.current) {
                typeof onImageResize === 'function' && onImageResize(imgRef.current.offsetWidth, imgRef.current.offsetHeight);
            }
        },
    });
    var handleOnImageLoaded = function () {
        if (legacyMode && imgRef.current) {
            setNaturalWidth(imgRef.current.naturalWidth || 1);
            setNaturalHeight(imgRef.current.naturalHeight || 1);
        }
        setIsImageLoaded(true);
        typeof onImageLoaded === 'function' && onImageLoaded();
    };
    var componentStyle = {
        width: 'auto',
        height: 'auto',
        paddingTop: "".concat((100 / ratioWidth) * ratioHeight, "%"),
        background: background,
    };
    if (width) {
        componentStyle.width = width;
    }
    if (height) {
        componentStyle.height = height;
        componentStyle.paddingTop = '0px';
    }
    if (width && !height && ratio) {
        componentStyle.paddingTop = "calc(".concat(width, " / ").concat(ratioWidth / ratioHeight, ")");
    }
    if (!width && height && ratio) {
        componentStyle.width = "calc(".concat(height, " * ").concat(ratioWidth / ratioHeight, ")");
    }
    var imageWidth = '100%';
    var imageHeight = '100%';
    if (legacyMode) {
        var componentRatioFloat = 1;
        if (ratio) {
            componentRatioFloat = ratioWidth / ratioHeight;
        }
        else if (componentWidth && componentHeight) {
            componentRatioFloat = componentWidth / componentHeight;
        }
        else if (wrapperRef.current) {
            componentRatioFloat = wrapperRef.current.offsetWidth / wrapperRef.current.offsetHeight;
        }
        if (componentRatioFloat > naturalWidth / naturalHeight) {
            imageWidth = '100%';
            imageHeight = 'auto';
        }
        else {
            imageWidth = 'auto';
            imageHeight = '100%';
        }
    }
    var imageStyle = {
        width: imageWidth,
        height: imageHeight,
    };
    var data = {
        'data-loaded': isImageLoaded || false,
    };
    var image = src ? (jsx(StyledImage$1, __assign({ ref: imgRef, src: src, srcSet: srcSet, sizes: sizes, alt: alt, width: imageWidth, height: imageHeight, fadeIn: fadeIn, legacyMode: legacyMode, onLoad: handleOnImageLoaded, style: imageStyle }, data))) : null;
    return (jsx(StyledComponent$1, __assign({ style: componentStyle }, data, { children: jsx(StyledWrapper$1, __assign({ ref: wrapperRef }, { children: children ? (jsxs("picture", { children: [children, image] })) : (jsx(Fragment, { children: srcWebP ? (jsxs("picture", { children: [jsx("source", { srcSet: srcWebP, type: "image/webp" }), image] })) : (image) })) })) })));
};

var ChevronUp = (jsx("svg", __assign({ viewBox: "0 0 24 24" }, { children: jsx("path", { fill: "currentColor", d: "M7.41,15.41L12,10.83L16.59,15.41L18,14L12,8L6,14L7.41,15.41Z" }) })));

var ChevronDown = (jsx("svg", __assign({ viewBox: "0 0 24 24" }, { children: jsx("path", { fill: "currentColor", d: "M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z" }) })));

var StyledDropdown = styled.div(function (_a) {
    var jss = _a.jss;
    return jss;
});
var StyledMenuWrapper$1 = styled.div({
    position: 'relative',
    width: '100%',
    height: 0,
});
var StyledMenu$1 = styled.div(function (_a) {
    var jss = _a.jss;
    return jss;
});
var Dropdown = function (_a) {
    var layout = _a.layout, label = _a.label, _b = _a.block, block = _b === void 0 ? false : _b, iconOpen = _a.iconOpen, iconClose = _a.iconClose, _c = _a.disabled, disabled = _c === void 0 ? false : _c, _d = _a.show, show = _d === void 0 ? false : _d, children = _a.children, onChange = _a.onChange, theme = _a.theme;
    var _e = useState(false), isOpen = _e[0], setIsOpen = _e[1];
    var refComponent = useRef(null);
    var handleOnClickDocument = function (e) {
        var _a;
        if (!((_a = refComponent === null || refComponent === void 0 ? void 0 : refComponent.current) === null || _a === void 0 ? void 0 : _a.contains(e.target))) {
            setIsOpen(false);
        }
    };
    useEffect(function () {
        document.addEventListener('click', handleOnClickDocument);
        return function () {
            document.removeEventListener('click', handleOnClickDocument);
        };
    }, []);
    useEffect(function () {
        if (typeof onChange === 'function')
            onChange(isOpen);
    }, [isOpen]);
    useEffect(function () {
        setIsOpen(show);
    }, [show]);
    var handleOnClickButton = function () {
        setIsOpen(!isOpen);
    };
    var _iconOpen = iconOpen || ChevronDown;
    var _iconClose = iconClose || ChevronUp;
    var jssDropdown = {
        position: 'relative',
        display: block ? 'block' : 'inline-block',
    };
    if (theme === null || theme === void 0 ? void 0 : theme.root) {
        jssDropdown = deepmerge(jssDropdown, theme === null || theme === void 0 ? void 0 : theme.root);
    }
    var jssMenu = {
        position: 'absolute',
        zIndex: 10,
        top: 0,
        left: 0,
        minWidth: '100%',
        boxSizing: 'border-box',
        background: 'white',
        boxShadow: COMMON_STYLES.boxShadowDropdown,
    };
    if (theme === null || theme === void 0 ? void 0 : theme.menu) {
        jssMenu = deepmerge(jssMenu, theme === null || theme === void 0 ? void 0 : theme.menu);
    }
    else {
        jssMenu = deepmerge(jssMenu, {
            padding: '5px',
            borderRadius: COMMON_STYLES.borderRadius,
        });
    }
    return (jsxs(StyledDropdown, __assign({ ref: refComponent, jss: jssDropdown }, { children: [jsx(Button, { layout: layout, label: label, iconRight: isOpen ? _iconClose : _iconOpen, onClick: handleOnClickButton, block: block, justify: "space-between", disabled: disabled, theme: {
                    root: theme === null || theme === void 0 ? void 0 : theme.button,
                } }), isOpen && (jsx(StyledMenuWrapper$1, { children: jsx(StyledMenu$1, __assign({ jss: jssMenu }, { children: children })) }))] })));
};

var StyledGridItem = styled.div(function (_a) {
    var jss = _a.jss;
    return jss;
});
var StyledGridItemCell = styled.div(function (_a) {
    var jss = _a.jss;
    return jss;
});
var GridItem = function (_a) {
    var _b = _a.grow, grow = _b === void 0 ? false : _b, width = _a.width, background = _a.background, gridSpacing = _a.gridSpacing, gridHSpacing = _a.gridHSpacing, gridVSpacing = _a.gridVSpacing, _c = _a.gridNowrap, gridNowrap = _c === void 0 ? false : _c, children = _a.children, theme = _a.theme;
    var paddingRight = undefined;
    var paddingBottom = undefined;
    if (SPACINGS[gridSpacing]) {
        paddingRight = SPACINGS[gridSpacing];
        paddingBottom = SPACINGS[gridSpacing];
    }
    else if (gridSpacing) {
        paddingRight = gridSpacing;
        paddingBottom = gridSpacing;
    }
    if (SPACINGS[gridHSpacing]) {
        paddingRight = SPACINGS[gridHSpacing];
    }
    else if (gridHSpacing) {
        paddingRight = gridHSpacing;
    }
    if (SPACINGS[gridVSpacing]) {
        paddingBottom = SPACINGS[gridVSpacing];
    }
    else if (gridVSpacing) {
        paddingBottom = gridVSpacing;
    }
    if (gridNowrap) {
        paddingBottom = 0;
    }
    var jssItem = {
        width: width,
        flexBasis: width,
        flexGrow: grow ? 1 : 0,
        flexShrink: 0,
        boxSizing: 'border-box',
        display: 'flex',
        paddingRight: paddingRight,
        paddingBottom: paddingBottom,
    };
    var jssItemCell = {
        flexGrow: 1,
        background: COLORS[background] || background,
    };
    if (theme === null || theme === void 0 ? void 0 : theme.root) {
        jssItemCell = __assign(__assign({}, jssItemCell), theme === null || theme === void 0 ? void 0 : theme.root);
    }
    return (jsx(StyledGridItem, __assign({ jss: jssItem }, { children: jsx(StyledGridItemCell, __assign({ jss: jssItemCell }, { children: children })) })));
};

var StyledGridWrapper = styled.div(function (_a) {
    var jss = _a.jss;
    return jss;
});
var StyledGrid = styled.div(function (_a) {
    var jss = _a.jss;
    return jss;
});
var Grid = function (_a) {
    var _b = _a.columns, columns = _b === void 0 ? 12 : _b, spacing = _a.spacing, hSpacing = _a.hSpacing, vSpacing = _a.vSpacing, justifyContent = _a.justifyContent, justify = _a.justify, alignItems = _a.alignItems, alignContent = _a.alignContent, _c = _a.nowrap, nowrap = _c === void 0 ? false : _c, children = _a.children, theme = _a.theme;
    var aChildren = React.Children.toArray(children);
    var jssWrapper = {
        display: 'flex',
    };
    if (theme === null || theme === void 0 ? void 0 : theme.root) {
        jssWrapper = deepmerge(jssWrapper, theme === null || theme === void 0 ? void 0 : theme.root);
    }
    var marginRight = undefined;
    var marginBottom = undefined;
    if (SPACINGS[spacing]) {
        marginRight = "-".concat(SPACINGS[spacing]);
        marginBottom = "-".concat(SPACINGS[spacing]);
    }
    else if (spacing) {
        marginRight = "-".concat(spacing);
        marginBottom = "-".concat(spacing);
    }
    if (SPACINGS[hSpacing]) {
        marginRight = "-".concat(SPACINGS[hSpacing]);
    }
    else if (hSpacing) {
        marginRight = "-".concat(hSpacing);
    }
    if (SPACINGS[vSpacing]) {
        marginBottom = "-".concat(SPACINGS[vSpacing]);
    }
    else if (vSpacing) {
        marginBottom = "-".concat(vSpacing);
    }
    var jssGrid = {
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: justifyContent || justify,
        alignItems: alignItems,
        alignContent: alignContent,
        marginRight: marginRight,
        marginBottom: marginBottom,
    };
    if (nowrap) {
        jssGrid.flexWrap = 'nowrap';
        jssGrid.marginBottom = undefined;
    }
    return (jsx(StyledGridWrapper, __assign({ jss: jssWrapper }, { children: jsx(StyledGrid, __assign({ jss: jssGrid }, { children: aChildren.map(function (child, i) {
                var _a;
                var childAsReactElement = child;
                if (childAsReactElement.type === GridItem) {
                    var childProps = childAsReactElement.props || {};
                    var width = childProps.width;
                    if (!childProps.width || childProps.width === 'auto') {
                        if (childProps.grow) {
                            width = '0%';
                        }
                        else {
                            var colspan = childProps.cols || childProps.colspan || 1;
                            width = "".concat((100 / parseFloat(String(columns))) * parseFloat(colspan), "%");
                        }
                    }
                    return React.cloneElement(childAsReactElement, {
                        key: String(i),
                        width: width,
                        gridSpacing: spacing,
                        gridHSpacing: hSpacing,
                        gridVSpacing: vSpacing,
                        gridNowrap: nowrap,
                        theme: {
                            root: deepmerge((theme === null || theme === void 0 ? void 0 : theme.item) || {}, ((_a = childProps === null || childProps === void 0 ? void 0 : childProps.theme) === null || _a === void 0 ? void 0 : _a.root) || {}),
                        },
                    });
                }
                return null;
            }) })) })));
};

var Group = function (_a) {
    var display = _a.display, flexDirection = _a.flexDirection, vertical = _a.vertical, flexWrap = _a.flexWrap, nowrap = _a.nowrap, gap = _a.gap, spacing = _a.spacing, columnGap = _a.columnGap, hSpacing = _a.hSpacing, rowGap = _a.rowGap, vSpacing = _a.vSpacing, justify = _a.justify, justifyContent = _a.justifyContent, children = _a.children, props = __rest(_a, ["display", "flexDirection", "vertical", "flexWrap", "nowrap", "gap", "spacing", "columnGap", "hSpacing", "rowGap", "vSpacing", "justify", "justifyContent", "children"]);
    return (jsx(UiDiv, __assign({ display: display || 'flex', flexDirection: flexDirection || vertical ? 'column' : undefined, flexWrap: flexWrap || nowrap ? 'nowrap' : 'wrap', gap: gap || spacing, columnGap: columnGap || hSpacing, rowGap: rowGap || vSpacing, justifyContent: justifyContent || justify }, props, { children: children })));
};

var GroupItem = function (_a) {
    var grow = _a.grow, flexGrow = _a.flexGrow, children = _a.children, props = __rest(_a, ["grow", "flexGrow", "children"]);
    return (jsx(UiDiv, __assign({ flexGrow: flexGrow || grow ? 1 : undefined }, props, { children: children })));
};

var StyledRatioBox = styled.div({
    position: 'relative',
});
var StyledRatioInnerBox = styled.div({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
});
var RatioBox = function (_a) {
    var ratio = _a.ratio, width = _a.width, height = _a.height, background = _a.background, children = _a.children;
    var _ratio = ratio || '1:1';
    var _b = _ratio.split(':'), w = _b[0], h = _b[1];
    var ratioWidth = parseFloat(w) || 1;
    var ratioHeight = parseFloat(h) || 1;
    var ratioFloat = ratioWidth / ratioHeight;
    var inlineStyle = {
        width: width,
        height: height,
        paddingTop: ratio && !height ? "".concat((100 / ratioWidth) * ratioHeight, "%") : undefined,
        background: background,
    };
    if (width && !height && ratio) {
        inlineStyle.paddingTop = "calc(".concat(width, " / ").concat(ratioFloat, ")");
    }
    if (!width && height && ratio) {
        inlineStyle.width = "calc(".concat(height, " * ").concat(ratioFloat, ")");
    }
    return (jsx(StyledRatioBox, __assign({ style: inlineStyle }, { children: jsx(StyledRatioInnerBox, { children: children }) })));
};

var ImageSource = function () {
    return null;
};
var ImageSource$1 = React.memo(ImageSource);

var useImage = function (_a) {
    var imageElementRef = _a.imageElementRef, children = _a.children;
    var observerRef = useRef(null);
    var _b = useState(undefined), persistedImageSources = _b[0], setPersistedImageSources = _b[1];
    var _c = useState(undefined), matchedImageSource = _c[0], setMatchedImageSource = _c[1];
    var _d = useState(false), isImageSourceWebP = _d[0], setIsImageSourceWebP = _d[1];
    var setImageSources = function (imageSources) {
        if (JSON.stringify(imageSources) !== JSON.stringify(persistedImageSources)) {
            setPersistedImageSources(imageSources);
        }
    };
    useEffect(function () {
        if (children) {
            var isWebP_1 = false;
            var imageSources = React.Children.map(children, function (child) {
                var childAsReactElement = child;
                if (childAsReactElement.type === ImageSource$1) {
                    var childProps = childAsReactElement.props;
                    if (childProps.srcWebP) {
                        isWebP_1 = true;
                    }
                    return childProps;
                }
                return null;
            });
            setIsImageSourceWebP(isWebP_1);
            if (imageSources && imageSources.length > 0) {
                setImageSources(imageSources);
            }
            else {
                setImageSources(undefined);
            }
        }
        else {
            setImageSources(undefined);
        }
    }, [children]);
    useEffect(function () {
        var handleResizeObserverCallback = function (entries) {
            var _a, _b;
            if ((imageElementRef === null || imageElementRef === void 0 ? void 0 : imageElementRef.current) && entries[0]) {
                var imageWidth_1 = ((_a = entries[0].contentRect) === null || _a === void 0 ? void 0 : _a.width) || (imageElementRef === null || imageElementRef === void 0 ? void 0 : imageElementRef.current.offsetWidth);
                var imageHeight_1 = ((_b = entries[0].contentRect) === null || _b === void 0 ? void 0 : _b.height) || (imageElementRef === null || imageElementRef === void 0 ? void 0 : imageElementRef.current.offsetHeight);
                setMatchedImageSource(persistedImageSources === null || persistedImageSources === void 0 ? void 0 : persistedImageSources.find(function (imageSource) {
                    var minWidth = imageSource.minWidth, minHeight = imageSource.minHeight;
                    return ((minWidth !== undefined ? imageWidth_1 >= minWidth : true) &&
                        (minHeight !== undefined ? imageHeight_1 >= minHeight : true));
                }));
            }
        };
        if ((imageElementRef === null || imageElementRef === void 0 ? void 0 : imageElementRef.current) && persistedImageSources && persistedImageSources.length > 0) {
            if (observerRef.current) {
                observerRef.current.unobserve(imageElementRef === null || imageElementRef === void 0 ? void 0 : imageElementRef.current);
            }
            if (ResizeObserver) {
                observerRef.current = new ResizeObserver(handleResizeObserverCallback);
                observerRef.current.observe(imageElementRef === null || imageElementRef === void 0 ? void 0 : imageElementRef.current);
            }
        }
    }, [imageElementRef, persistedImageSources]);
    return {
        areImageSourcesAvailable: !!persistedImageSources,
        matchedImageSource: matchedImageSource,
        isImageSourceWebP: isImageSourceWebP,
    };
};

var StyledImage = styled.img(function (_a) {
    var styledCSSObject = _a.styledCSSObject;
    return styledCSSObject;
});
var Image = function (_a) {
    var src = _a.src, srcWebP = _a.srcWebP, alt = _a.alt, display = _a.display, width = _a.width, minWidth = _a.minWidth, maxWidth = _a.maxWidth, height = _a.height, minHeight = _a.minHeight, maxHeight = _a.maxHeight, ratio = _a.ratio, _b = _a.verticalAlign, verticalAlign = _b === void 0 ? 'top' : _b, objectFit = _a.objectFit, objectPosition = _a.objectPosition, srcSet = _a.srcSet, sizes = _a.sizes, background = _a.background, transition = _a.transition, children = _a.children, onImageLoaded = _a.onImageLoaded;
    var imageElementRef = useRef(null);
    var _c = useImage({
        imageElementRef: imageElementRef,
        children: children,
    }), areImageSourcesAvailable = _c.areImageSourcesAvailable, matchedImageSource = _c.matchedImageSource, isImageSourceWebP = _c.isImageSourceWebP;
    var handleOnImageLoaded = function (e) {
        if (typeof onImageLoaded === 'function')
            onImageLoaded(e);
    };
    var styledCSSObject = {
        display: display,
        width: ratio ? '100%' : width,
        minWidth: ratio ? '100%' : minWidth,
        maxWidth: ratio ? '100%' : maxWidth,
        height: ratio ? '100%' : height,
        minHeight: ratio ? '100%' : minHeight,
        maxHeight: ratio ? '100%' : maxHeight,
        verticalAlign: verticalAlign,
        objectFit: objectFit || (ratio ? 'cover' : undefined),
        objectPosition: objectPosition,
        background: background,
        transition: transition,
    };
    var image = src ? (jsx(StyledImage, { ref: imageElementRef, src: (matchedImageSource === null || matchedImageSource === void 0 ? void 0 : matchedImageSource.src) || src, alt: alt, width: width, height: height, srcSet: srcSet, sizes: sizes, styledCSSObject: styledCSSObject, onLoad: handleOnImageLoaded })) : null;
    var imageComponent;
    if (areImageSourcesAvailable && isImageSourceWebP) {
        imageComponent = (jsxs("picture", { children: [jsx("source", { srcSet: (matchedImageSource === null || matchedImageSource === void 0 ? void 0 : matchedImageSource.srcWebP) || srcWebP, type: "image/webp" }), image] }));
    }
    else if (areImageSourcesAvailable) {
        imageComponent = image;
    }
    else if (srcWebP) {
        imageComponent = (jsxs("picture", { children: [jsx("source", { srcSet: srcWebP, type: "image/webp" }), image] }));
    }
    else if (children) {
        imageComponent = (jsxs("picture", { children: [children, image] }));
    }
    else {
        imageComponent = image;
    }
    if (ratio) {
        return (jsx(RatioBox, __assign({ ratio: ratio, width: width, height: height, background: background }, { children: imageComponent })));
    }
    return imageComponent;
};

var StyledWrapper = styled.span(function (_a) {
    var jss = _a.jss;
    return jss;
});
var InlineSVG = function (_a) {
    var _b = _a.width, width = _b === void 0 ? '24px' : _b, _c = _a.height, height = _c === void 0 ? 'auto' : _c, color = _a.color, children = _a.children;
    var jss = {
        display: 'inline-block',
        width: width,
        height: height,
        color: COLORS[color] || color,
        svg: {
            width: '100%',
            height: '100%',
            verticalAlign: 'top',
        },
    };
    return jsx(StyledWrapper, __assign({ jss: jss }, { children: children }));
};

var Input = function (_a) {
    var type = _a.type, value = _a.value, width = _a.width, block = _a.block, useDefaultStyle = _a.useDefaultStyle, theme = _a.theme, ariaLabel = _a.ariaLabel, blurOnEnter = _a.blurOnEnter, onChange = _a.onChange, onKeyPress = _a.onKeyPress, onEnter = _a.onEnter, props = __rest(_a, ["type", "value", "width", "block", "useDefaultStyle", "theme", "ariaLabel", "blurOnEnter", "onChange", "onKeyPress", "onEnter"]);
    var styledTheme = useTheme();
    var _b = useState(value), draftValue = _b[0], setDraftValue = _b[1];
    var handleOnKeyPress = function (e) {
        var _a;
        if (onKeyPress) {
            onKeyPress(e);
        }
        if (onEnter && (e.code === 'Enter' || e.key === 'Enter')) {
            onEnter(e);
            if (blurOnEnter) {
                (_a = e.currentTarget) === null || _a === void 0 ? void 0 : _a.blur();
            }
        }
    };
    var defaultStyle = useDefaultStyle && !!styledTheme
        ? __assign({ appearance: 'none', boxSizing: 'border-box', background: 'white', padding: '10px 15px', boxShadow: "inset 0px 0px 0px 1px ".concat(styledTheme.colors.grey), border: 0, borderRadius: '3px', fontFamily: '"Source Sans Pro", sans-serif', fontSize: '16px', lineHeight: '22px', color: styledTheme.colors.dark, '&::placeholder': {
                fontStyle: 'italic',
                color: styledTheme.colors.greyText,
            }, '&:hover': {
                boxShadow: "inset 0px 0px 0px 1px ".concat(styledTheme.colors.greyHover),
            }, '&:focus': {
                outline: 0,
                boxShadow: "inset 0px 0px 0px 2px ".concat(styledTheme.colors.primary),
            }, '&:disabled': {
                opacity: 0.5,
                cursor: 'default',
                pointerEvents: 'none',
            } }, theme === null || theme === void 0 ? void 0 : theme.root) : undefined;
    var csso = !!defaultStyle && (theme === null || theme === void 0 ? void 0 : theme.root) ? deepmerge(defaultStyle, theme === null || theme === void 0 ? void 0 : theme.root) : defaultStyle || (theme === null || theme === void 0 ? void 0 : theme.root);
    return (jsx(UiInput, __assign({ type: type || 'text', value: draftValue || '', width: block ? '100%' : width, csso: csso, "aria-label": ariaLabel, onChange: function (e) {
            setDraftValue(e.target.value);
            if (onChange) {
                onChange(e);
            }
        }, onKeyPress: handleOnKeyPress }, props)));
};

var StyledRoot = styled.div({
    position: 'relative',
    '&[data-disabled="true"]': {
        opacity: 0.5,
        pointerEvents: 'none',
    },
});
var StyledRadio = styled.div(function (_a) {
    var jss = _a.jss;
    return jss;
});
var StyledInput$1 = styled.input({
    appearance: 'none',
    position: 'absolute',
    top: 0,
    left: 0,
    height: 0,
    width: 0,
    margin: 0,
    padding: 0,
    fontSize: 0,
    lineHeight: 0,
    opacity: 0,
});
var StyledLabel = styled.span(function (_a) {
    var jss = _a.jss;
    return jss;
});
var Radio = function (_a) {
    var id = _a.id, name = _a.name, value = _a.value, label = _a.label, _b = _a.checked, checked = _b === void 0 ? false : _b, _c = _a.disabled, disabled = _c === void 0 ? false : _c, ariaLabel = _a.ariaLabel, _d = _a.spacing, spacing = _d === void 0 ? 'xs' : _d, onChange = _a.onChange, theme = _a.theme;
    var _e = useState(checked), isChecked = _e[0], setIsChecked = _e[1];
    var _f = useState(false), isFocus = _f[0], setIsFocus = _f[1];
    var refInput = useRef(null);
    useEffect(function () {
        setIsChecked(checked);
    }, [checked]);
    var handleOnInputChange = function (e) {
        setIsChecked(e.target.checked);
        if (typeof onChange === 'function')
            onChange(e);
    };
    var handleOnClickRadio = function () {
        if (refInput && refInput.current) {
            refInput.current.click();
            refInput.current.focus();
        }
    };
    var handleOnFocus = function () {
        setIsFocus(true);
    };
    var handleOnBlur = function () {
        setIsFocus(false);
    };
    var data = {
        'data-focus': isFocus || undefined,
        'data-checked': isChecked || undefined,
        'data-disabled': disabled || undefined,
    };
    var jssRadio = {
        flex: '0 0 auto',
        width: '22px',
        height: '22px',
        border: "1px solid ".concat(COLORS.GREY_TEXT),
        borderRadius: '50%',
        boxSizing: 'border-box',
        background: 'white',
        cursor: 'pointer',
        '&[data-checked="true"]': {
            background: COLORS.DARK,
            boxShadow: 'inset 0 0 0 5px white',
        },
    };
    if (theme === null || theme === void 0 ? void 0 : theme.radio) {
        jssRadio = deepmerge(jssRadio, theme.radio);
    }
    else {
        jssRadio = deepmerge(jssRadio, {
            color: COLORS.DARK,
            '&[data-focus="true"]': {
                borderColor: COLORS.DARK,
            },
            '&[data-checked="true"]': {
                background: COLORS.PRIMARY,
            },
        });
    }
    var jssLabel = __assign({ display: 'inline-block' }, TEXTSTYLE_MEDIUM);
    if (theme === null || theme === void 0 ? void 0 : theme.label) {
        jssLabel = deepmerge(jssLabel, theme.label);
    }
    return (jsxs(StyledRoot, __assign({}, data, { children: [jsx(StyledInput$1, { ref: refInput, type: "radio", id: id, name: name, value: value, checked: isChecked, "aria-label": (ariaLabel || label), disabled: disabled, onChange: handleOnInputChange, onFocus: handleOnFocus, onBlur: handleOnBlur }), jsxs(GroupLegacy, __assign({ nowrap: true, spacing: spacing }, { children: [jsx(StyledRadio, __assign({ jss: jssRadio, onClick: handleOnClickRadio }, data)), label && (jsx(StyledLabel, __assign({ jss: jssLabel }, data, { children: label })))] }))] })));
};

var RadioGroup = function (_a) {
    var value = _a.value, spacing = _a.spacing, _b = _a.vertical, vertical = _b === void 0 ? false : _b, children = _a.children, onChange = _a.onChange, theme = _a.theme;
    var aChildren = React.Children.toArray(children);
    var _c = useState(value), selectedValue = _c[0], setSelectedValue = _c[1];
    useEffect(function () {
        setSelectedValue(value);
    }, [value]);
    var handleOnChangeRadio = function (e) {
        setSelectedValue(e.target.value);
        if (typeof onChange === 'function')
            onChange(e.target.value);
    };
    return (jsx(GroupLegacy, __assign({ spacing: spacing, vertical: vertical }, { children: aChildren.map(function (child, i) {
            var childAsReactElement = child;
            var childProps = childAsReactElement.props || {};
            if (childAsReactElement.type === Radio) {
                return React.cloneElement(childAsReactElement, {
                    key: String(i),
                    checked: childProps.value === selectedValue,
                    theme: deepmerge((theme === null || theme === void 0 ? void 0 : theme.item) || {}, childProps.theme || {}),
                    onChange: function (e) { return handleOnChangeRadio(e); },
                });
            }
            return null;
        }) })));
};

var SelectOption = function (_a) {
    var value = _a.value, label = _a.label;
    return (jsxs("div", { children: [value, ": ", label] }));
};

var StyledSelect = styled.div(function (_a) {
    var jss = _a.jss;
    return jss;
});
var StyledMenuWrapper = styled.div({
    position: 'relative',
    width: '100%',
    height: 0,
});
var StyledMenu = styled.div(function (_a) {
    var jss = _a.jss;
    return jss;
});
var Select = function (_a) {
    var layout = _a.layout, label = _a.label, value = _a.value, _b = _a.block, block = _b === void 0 ? false : _b, iconOpen = _a.iconOpen, iconClose = _a.iconClose, _c = _a.disabled, disabled = _c === void 0 ? false : _c, children = _a.children, onSelect = _a.onSelect, theme = _a.theme;
    var _d = useState(value), valueLocal = _d[0], setValueLocal = _d[1];
    var _e = useState(label), labelLocal = _e[0], setLabelLocal = _e[1];
    var _f = useState(false), isOpen = _f[0], setIsOpen = _f[1];
    var refComponent = useRef(null);
    var aChildren = React.Children.toArray(children);
    var handleOnClickDocument = function (e) {
        if (refComponent && refComponent.current && !refComponent.current.contains(e.target)) {
            setIsOpen(false);
        }
    };
    useEffect(function () {
        document.addEventListener('click', handleOnClickDocument);
        return function () {
            document.removeEventListener('click', handleOnClickDocument);
        };
    }, []);
    useEffect(function () {
        setValueLocal(value);
    }, [value]);
    useEffect(function () {
        setLabelLocal(label);
    }, [label]);
    var handleOnClickButton = function () {
        setIsOpen(!isOpen);
    };
    var handleOnClickOption = function (optionValue, optionLabel) {
        setIsOpen(false);
        setValueLocal(optionValue);
        setLabelLocal(optionLabel);
        if (typeof onSelect === 'function')
            onSelect(optionValue, optionLabel);
    };
    var _iconOpen = iconOpen || ChevronDown;
    var _iconClose = iconClose || ChevronUp;
    var jssSelect = {
        position: 'relative',
        display: block ? 'block' : 'inline-block',
    };
    if (theme === null || theme === void 0 ? void 0 : theme.root) {
        jssSelect = deepmerge(jssSelect, theme.root);
    }
    var jssMenu = {
        position: 'absolute',
        zIndex: 10,
        top: 0,
        left: 0,
        minWidth: '100%',
        boxSizing: 'border-box',
        background: 'white',
        boxShadow: COMMON_STYLES.boxShadowDropdown,
    };
    if (theme === null || theme === void 0 ? void 0 : theme.menu) {
        jssMenu = deepmerge(jssMenu, theme.menu);
    }
    else {
        jssMenu = deepmerge(jssMenu, {
            padding: '5px',
            borderRadius: COMMON_STYLES.borderRadius,
        });
    }
    return (jsxs(StyledSelect, __assign({ ref: refComponent, jss: jssSelect }, { children: [jsx(Button, { layout: layout, label: labelLocal, iconRight: isOpen ? _iconClose : _iconOpen, onClick: handleOnClickButton, block: block, justify: "space-between", disabled: disabled, theme: {
                    root: theme === null || theme === void 0 ? void 0 : theme.button,
                } }), isOpen && (jsx(StyledMenuWrapper, { children: jsx(StyledMenu, __assign({ jss: jssMenu }, { children: aChildren.map(function (child, i) {
                        var childAsReactElement = child;
                        if (childAsReactElement.type === SelectOption) {
                            var childProps_1 = childAsReactElement.props || {};
                            var selected = childProps_1.value === valueLocal;
                            return (jsx(Button, { layout: "option", label: childProps_1.label, block: true, justify: "flex-start", active: selected, onClick: function () { return handleOnClickOption(childProps_1.value, childProps_1.label); }, theme: {
                                    root: theme === null || theme === void 0 ? void 0 : theme.option,
                                } }, String(i)));
                        }
                        return null;
                    }) })) }))] })));
};

var StyledSwitch = styled.button(function (_a) {
    var jss = _a.jss;
    return jss;
});
var StyledTrack = styled.div(function (_a) {
    var jss = _a.jss;
    return jss;
});
var StyledThumb = styled.div(function (_a) {
    var jss = _a.jss;
    return jss;
});
var Switch = function (_a) {
    var _b = _a.checked, checked = _b === void 0 ? false : _b, _c = _a.disabled, disabled = _c === void 0 ? false : _c, ariaLabel = _a.ariaLabel, onChange = _a.onChange, theme = _a.theme;
    var _d = useState(checked), isChecked = _d[0], setIsChecked = _d[1];
    var _e = useState(false), isFocus = _e[0], setIsFocus = _e[1];
    var _f = useState(false), isHover = _f[0], setIsHover = _f[1];
    useEffect(function () {
        setIsChecked(checked);
    }, [checked]);
    var handleOnClick = function () {
        setIsChecked(!isChecked);
        if (typeof onChange === 'function')
            onChange(!isChecked);
    };
    var handleOnMouseLeave = function (e) {
        setIsHover(false);
        e.currentTarget.blur();
    };
    var data = {
        'data-checked': isChecked || undefined,
        'data-disabled': disabled || undefined,
        'data-focus': isFocus || undefined,
        'data-hover': isHover || undefined,
    };
    var jssSwitch = {
        appearance: 'none',
        display: 'flex',
        alignItems: 'center',
        margin: 0,
        padding: 0,
        border: 0,
        background: 'transparent',
        position: 'relative',
        cursor: 'pointer',
        '&[data-disabled]': {
            opacity: 0.5,
            cursor: 'default',
            pointerEvents: 'none',
        },
    };
    if (theme === null || theme === void 0 ? void 0 : theme.root) {
        jssSwitch = deepmerge(jssSwitch, theme.root);
    }
    else {
        jssSwitch = deepmerge(jssSwitch, {
            '&:focus': {
                outline: 0,
            },
        });
    }
    var jssTrack = {
        display: 'inline-block',
        width: '40px',
        height: '20px',
        background: COLORS.grey,
        borderRadius: '10px',
    };
    if (theme === null || theme === void 0 ? void 0 : theme.track) {
        jssTrack = deepmerge(jssTrack, theme.track);
    }
    else {
        jssTrack = deepmerge(jssTrack, {
            '&[data-focus]': {
                background: COLORS.dark,
            },
            '&[data-hover]': {
                background: COLORS.grey,
            },
        });
    }
    var jssThumb = {
        display: 'inline-block',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '20px',
        height: '20px',
        borderRadius: '10px',
        background: COLORS.greyText,
        transition: 'left 0.2s ease',
        '&[data-checked]': {
            left: '20px',
            background: COLORS.primary,
        },
    };
    if (theme === null || theme === void 0 ? void 0 : theme.thumb) {
        jssThumb = deepmerge(jssThumb, theme.thumb);
    }
    return (jsxs(StyledSwitch, __assign({ jss: jssSwitch, "aria-label": ariaLabel, disabled: disabled, onClick: handleOnClick, onFocus: function () { return setIsFocus(true); }, onBlur: function () { return setIsFocus(false); }, onMouseEnter: function () { return setIsHover(true); }, onMouseLeave: handleOnMouseLeave }, data, { children: [jsx(StyledTrack, __assign({ jss: jssTrack }, data)), jsx(StyledThumb, __assign({ jss: jssThumb }, data))] })));
};

var StyledText = styled.div(function (_a) {
    var jss = _a.jss;
    return jss;
});
var Text = function (_a) {
    var size = _a.size, color = _a.color, fontSize = _a.fontSize, lineHeight = _a.lineHeight, textAlign = _a.textAlign, className = _a.className, children = _a.children;
    var baseStyle = size === 'small' ? TEXTSTYLE_SMALL : TEXTSTYLE_MEDIUM;
    var jss = __assign(__assign({}, baseStyle), { color: COLORS[color] || color || COLORS.DARK, textAlign: textAlign, fontSize: fontSize || baseStyle.fontSize, lineHeight: lineHeight || baseStyle.lineHeight });
    return (jsx(StyledText, __assign({ jss: jss, className: className }, { children: children })));
};

var StyledComponent = styled.div(function (_a) {
    var jss = _a.jss;
    return jss;
});
var StyledInput = styled.input({
    display: 'none',
});
var UploadButton = function (props) {
    var accept = props.accept, multiple = props.multiple, theme = props.theme, onChange = props.onChange;
    var refInputFile = useRef(null);
    var handleOnClickButton = function () {
        if (refInputFile && refInputFile.current) {
            refInputFile.current.click();
        }
    };
    var jss = {
        display: 'inline-block',
    };
    if (theme === null || theme === void 0 ? void 0 : theme.root) {
        jss = deepmerge(jss, theme.root);
    }
    var buttonProps = __assign(__assign({}, props), { theme: {
            root: theme === null || theme === void 0 ? void 0 : theme.button,
        } });
    return (jsxs(StyledComponent, __assign({ jss: jss }, { children: [jsx(StyledInput, { ref: refInputFile, type: "file", accept: accept, multiple: multiple, onChange: onChange }), jsx(Button, __assign({}, buttonProps, { onClick: handleOnClickButton }))] })));
};

export { Block, Button, Checkbox, Collapse, CroppedImage, Dropdown, Grid, GridItem, Group, GroupItem, GroupItemLegacy, GroupLegacy, Image, InlineSVG, Input, Radio, RadioGroup, RatioBox, Select, SelectOption, Switch, Text, UiDiv, UiElement, UiFlex, UiInput, UiSpan, UiText, UploadButton };
